import { useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Casting tab (Mockup D #s5 + s18).
 *
 * Lista de personajes extraídos por IA del guion subido. Cada card incluye
 * avatar circular con inicial, badge de importancia, datos clave en grid
 * (edad · género · escenas · líneas), bloque destacado de casting notes y
 * edición inline de actor propuesto / estado / notas.
 *
 * Sin cambios funcionales — solo refresco visual a la paleta de marca
 * (gb-red, gb-paper, montserrat) y layout más limpio.
 */

const STATUSES = ["Por proponer", "Lista corta", "Pruebas", "Reservado", "Confirmado", "Descartado"];
const STATUS_COLORS = {
  "Por proponer": "bg-stone-200 text-stone-700",
  "Lista corta": "bg-amber-100 text-amber-900",
  "Pruebas": "bg-sky-100 text-sky-900",
  "Reservado": "bg-violet-100 text-violet-900",
  "Confirmado": "bg-emerald-100 text-emerald-900",
  "Descartado": "bg-red-100 text-red-900",
};
const IMPORTANCE_BADGE = {
  "protagonista": "bg-gb-red text-white",
  "secundario": "bg-gb-black text-white",
  "episódico": "bg-stone-500 text-white",
  "figurante": "bg-stone-200 text-stone-700",
};

export default function CastingTab({ project, projectId, onReload }) {
  const { user } = useAuth();
  const uploaded = project?.script?.uploaded_script;
  const casting = project?.casting;

  const [analyzing, setAnalyzing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("Todos");

  const analyze = async () => {
    setAnalyzing(true); setError("");
    try {
      const r = await api.post(`/icaa/projects/${projectId}/ai/casting/analyze`);
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("No se obtuvo job_id");
      const startedAt = Date.now();
      const MAX_MS = 6 * 60 * 1000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 4000));
        const p = await api.get(`/icaa/ai/jobs/${jobId}`);
        if (p.data.status === "done") { await onReload?.(); return; }
        if (p.data.status === "error") {
          const err = p.data.error || "Error desconocido";
          if (err.toLowerCase().includes("budget")) {
            throw new Error("Se ha agotado el saldo de la IA. Pídele a Eduardo que recargue desde su perfil de Emergent (Universal Key → Add Balance).");
          }
          throw new Error(err);
        }
        if (Date.now() - startedAt > MAX_MS) throw new Error("La IA tardó más de 6 min. Inténtalo de nuevo más tarde.");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Error al analizar.");
    } finally { setAnalyzing(false); }
  };

  const uploadScript = async (file) => {
    if (!file) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/icaa/projects/${projectId}/script/upload`, {
        method: "POST", credentials: "include", body: fd,
      });
      if (!r.ok) throw new Error((await r.json())?.detail || "Error subiendo guion");
      await onReload?.();
    } catch (e) {
      setError(e.message);
    } finally { setUploading(false); }
  };

  if (!uploaded?.text) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-10 sm:px-8" data-testid="casting-section">
        <div className="rounded-2xl border border-dashed border-gb-paper bg-gb-surface p-10 text-center">
          <p className="gb-eyebrow">Casting</p>
          <h2 className="mt-2 gb-display text-3xl">Sube el guion para empezar</h2>
          <p className="mx-auto mt-3 max-w-md text-sm text-gb-muted">
            Cuando subas el guion (PDF, TXT o Fountain), la IA analizará el reparto: extraerá todos los personajes,
            su importancia, edad, descripción, arco y sugerencias para casting.
          </p>
          <label className="mt-6 inline-flex cursor-pointer items-center gap-2 rounded-full bg-gb-red px-6 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-white hover-gb-red"
                 data-testid="casting-upload-script-btn">
            <input type="file" accept=".pdf,.txt,.fountain,.fdx" hidden
                   onChange={(e) => uploadScript(e.target.files?.[0])} data-testid="casting-upload-input" />
            {uploading ? "Subiendo…" : "Subir guion"}
          </label>
          {error && <p className="mt-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="casting-error">{error}</p>}
        </div>
      </section>
    );
  }

  if (!casting?.characters?.length) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-10 sm:px-8" data-testid="casting-section">
        <div className="rounded-2xl border border-gb-paper bg-gb-surface p-8 text-center">
          <p className="gb-eyebrow">Casting</p>
          <h2 className="mt-2 gb-display text-3xl">Guion subido</h2>
          <p className="mt-2 text-sm text-gb-muted"><strong>{uploaded.filename}</strong> · {uploaded.char_count.toLocaleString()} caracteres</p>
          <p className="mx-auto mt-4 max-w-md text-sm text-gb-muted">
            Pulsa para analizar el reparto. La IA leerá el guion completo y desglosará todos los personajes.
          </p>
          <button onClick={analyze} disabled={analyzing} data-testid="casting-analyze-btn"
                  className="mt-6 rounded-full bg-gb-red px-6 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-white hover-gb-red disabled:opacity-50">
            {analyzing ? "Analizando con IA (puede tardar 1-2 min)…" : "✦ Analizar reparto con IA"}
          </button>
          {error && <p className="mt-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        </div>
      </section>
    );
  }

  const filtered = filter === "Todos"
    ? casting.characters
    : casting.characters.filter((c) => c.importance === filter);

  const counts = casting.characters.reduce((acc, c) => {
    acc[c.importance] = (acc[c.importance] || 0) + 1;
    return acc;
  }, {});

  return (
    <section className="mx-auto max-w-6xl px-4 py-6 sm:px-8 sm:py-8" data-testid="casting-section">
      {/* Header */}
      <div className="mb-6 rounded-2xl border border-gb-paper bg-gb-surface p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="gb-eyebrow">Casting</p>
            <h2 className="mt-1 gb-display text-2xl">{casting.total_characters} personajes detectados</h2>
            <p className="mt-1 text-[12px] text-gb-muted">
              Desde el guion {casting.source_filename || "subido"} · {casting.main_cast_count} protagonista{casting.main_cast_count === 1 ? "" : "s"}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <button onClick={analyze} disabled={analyzing} data-testid="casting-reanalyze-btn"
                    className="rounded-full border border-gb-paper px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-gb-black hover:border-gb-red hover:text-gb-red disabled:opacity-40">
              {analyzing ? "Re-analizando…" : "↻ Re-analizar"}
            </button>
            <label className="cursor-pointer text-[11px] text-gb-muted hover:text-gb-red underline-offset-2 hover:underline">
              <input type="file" accept=".pdf,.txt,.fountain,.fdx" hidden
                     onChange={(e) => uploadScript(e.target.files?.[0])} data-testid="casting-replace-script-input" />
              Reemplazar guion
            </label>
          </div>
        </div>
        {casting.ensemble_notes && (
          <p className="mt-4 rounded-md bg-gb-paper-soft px-3 py-2.5 text-[13px] italic text-gb-black/80">
            <strong className="not-italic text-gb-red">Conjunto:</strong> {casting.ensemble_notes}
          </p>
        )}
        {error && <p className="mt-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-1.5">
        <FilterPill label="Todos" count={casting.characters.length} active={filter === "Todos"} onClick={() => setFilter("Todos")} />
        {["protagonista", "secundario", "episódico", "figurante"].map((imp) => counts[imp] ? (
          <FilterPill key={imp} label={imp} count={counts[imp]} active={filter === imp} onClick={() => setFilter(imp)} />
        ) : null)}
      </div>

      {/* Grid */}
      <ul className="grid gap-3 md:grid-cols-2" data-testid="casting-list">
        {filtered.map((c) => (
          <CharacterCard key={c.id} character={c} projectId={projectId} onReload={onReload} adminMode={user?.role === "admin"} />
        ))}
      </ul>
    </section>
  );
}

// ─── CharacterCard ───────────────────────────────────────────────────────

function CharacterCard({ character, projectId, onReload }) {
  const [editing, setEditing] = useState(false);
  const [actorProposal, setActorProposal] = useState(character.actor_proposal || "");
  const [status, setStatus] = useState(character.status || "Por proponer");
  const [castingNotesExtra, setCastingNotesExtra] = useState(character.casting_notes || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/icaa/projects/${projectId}/casting/character/${character.id}`, {
        actor_proposal: actorProposal, status, casting_notes: castingNotesExtra,
      });
      setEditing(false);
      await onReload?.();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const initial = (character.name || "?").trim()[0]?.toUpperCase() || "?";
  const accentClass = character.importance === "protagonista" ? "bg-gb-red"
    : character.importance === "secundario" ? "bg-gb-black"
    : "bg-stone-500";

  return (
    <li className="rounded-2xl border border-gb-paper bg-gb-surface p-4 transition hover:border-gb-red/40"
        data-testid={`character-${character.id}`}>
      {/* Header con avatar */}
      <div className="flex items-start gap-3">
        <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-base font-semibold text-white ${accentClass}`}>
          {initial}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <h3 className="font-serif text-lg leading-tight text-gb-black">{character.name}</h3>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-widest ${IMPORTANCE_BADGE[character.importance]}`}>
              {character.importance}
            </span>
          </div>
          {/* Grid de datos */}
          <dl className="mt-2 grid grid-cols-4 gap-2 text-[11px]">
            <Stat label="Edad" value={character.age_range || "—"} />
            <Stat label="Género" value={character.gender || "—"} />
            <Stat label="Escenas" value={character.scene_count || 0} />
            <Stat label="Líneas" value={character.lines_count || 0} />
          </dl>
        </div>
      </div>

      {character.description && (
        <p className="mt-3 text-[13px] leading-snug text-gb-black/85">{character.description}</p>
      )}
      {character.arc && (
        <p className="mt-2 text-[12px] italic text-gb-muted">
          <strong className="not-italic text-gb-black/80">Arco:</strong> {character.arc}
        </p>
      )}
      {character.key_relationships && (
        <p className="mt-1 text-[12px] text-gb-muted">
          <strong className="text-gb-black/80">Relaciones:</strong> {character.key_relationships}
        </p>
      )}

      {character.casting_notes && !editing && (
        <p className="mt-3 rounded-md border-l-[3px] border-l-gb-red bg-gb-paper-soft/60 px-3 py-2 text-[12px] text-gb-black/85">
          <strong className="text-gb-red">Casting:</strong> {character.casting_notes}
        </p>
      )}

      {character.scenes?.length > 0 && (
        <p className="mt-2 text-[11px] text-gb-muted">
          <strong className="text-gb-black/70">Aparece en:</strong> {character.scenes.join(", ")}
        </p>
      )}

      {/* Status + actor + edit toggle */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-gb-paper pt-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${STATUS_COLORS[character.status] || STATUS_COLORS["Por proponer"]}`}>
            {character.status || "Por proponer"}
          </span>
          {character.actor_proposal && !editing && (
            <p className="text-[12px] text-gb-black/80">
              <strong className="text-gb-muted">Propuesta:</strong> {character.actor_proposal}
            </p>
          )}
        </div>
        <button onClick={() => setEditing((x) => !x)} data-testid={`character-edit-${character.id}`}
                className="rounded-full border border-gb-paper px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-gb-black/70 hover:border-gb-red hover:text-gb-red">
          {editing ? "Cancelar" : "Editar"}
        </button>
      </div>

      {editing && (
        <div className="mt-3 rounded-md border border-gb-paper bg-gb-paper-soft/40 p-3">
          <Field label="Propuesta de actor/actriz">
            <input value={actorProposal} onChange={(e) => setActorProposal(e.target.value)}
                   placeholder="Nombre del actor, agencia, contacto…"
                   data-testid={`character-actor-input-${character.id}`}
                   className="mt-1 w-full rounded-md border border-gb-paper bg-white px-3 py-2 text-sm outline-none focus:border-gb-red" />
          </Field>
          <Field label="Estado">
            <select value={status} onChange={(e) => setStatus(e.target.value)}
                    data-testid={`character-status-select-${character.id}`}
                    className="mt-1 w-full rounded-md border border-gb-paper bg-white px-3 py-2 text-sm outline-none focus:border-gb-red">
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Notas de casting">
            <textarea rows={3} value={castingNotesExtra} onChange={(e) => setCastingNotesExtra(e.target.value)}
                      data-testid={`character-notes-${character.id}`}
                      className="mt-1 w-full rounded-md border border-gb-paper bg-white px-3 py-2 text-sm outline-none focus:border-gb-red" />
          </Field>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setEditing(false)}
                    className="rounded-full border border-gb-paper px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-gb-muted hover:bg-gb-paper-soft">
              Cancelar
            </button>
            <button onClick={save} disabled={busy} data-testid={`character-save-${character.id}`}
                    className="rounded-full bg-gb-red px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-white hover-gb-red disabled:opacity-40">
              {busy ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <dt className="text-[9px] font-semibold uppercase tracking-[0.2em] text-gb-muted">{label}</dt>
    <dd className="font-serif text-sm text-gb-black tabular-nums">{value}</dd>
  </div>
);

const Field = ({ label, children }) => (
  <label className="mt-2 block first:mt-0">
    <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-gb-muted">{label}</span>
    {children}
  </label>
);

const FilterPill = ({ label, count, active, onClick }) => (
  <button onClick={onClick}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-widest transition ${
            active ? "bg-gb-black text-white"
                   : "border border-gb-paper bg-gb-surface text-gb-black/65 hover:border-gb-black/30 hover:text-gb-black"
          }`}>
    <span>{label}</span>
    <span className={`tabular-nums text-[10px] ${active ? "text-white/65" : "text-gb-muted"}`}>{count}</span>
  </button>
);
